import { FC, useMemo } from 'react';
import { TableActions } from 'components/Table';
import { usePagination } from 'further-ui/hooks';
import { Unarchive, Visibility, Archive } from '@mui/icons-material';
import { dateToLabel } from 'further-ui/utils';
import {
  formatTypeOfFailureString,
  formatCountryNamesString,
  furtherCased,
} from 'helpers/kyc/text-formatting';
import { sortBy } from 'lodash';
import { AmlMatchTypeLabels } from 'further-ui/labels';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import AmlResultInformation from './AmlResultInformation';
import { styled } from '@mui/material';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  results: Array<{
    caDocId: string;
    createdAt?: string;
    value: string;
    type: string;
    reasons: Array<string>;
    countryNames: Array<string>;
    archived?: boolean;
  }>;
  handleArchiveAmlHit: (caDocId: string) => void;
  handleUnarchiveAmlHit: (caDocId: string) => void;
  investorId: string;
  isLoading: boolean;
};

const TableContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(2),
}));

const StyledTable = styled(InteractiveTable)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
}));

const AmlResultsTable: FC<Props> = ({
  results,
  handleArchiveAmlHit,
  handleUnarchiveAmlHit,
  investorId,
  isLoading,
}) => {
  const [canEditAml] = useHasPermissions([RolePermission.EditInvestorKyc]);

  // Memoize columns to prevent unnecessary recreations
  const columns: Array<Column> = useMemo(
    () => [
      {
        field: 'createdAt',
        flex: 1,
        headerName: 'Date',
        sortable: false,
        renderCell: ({ row }) => dateToLabel(row.createdAt),
      },
      {
        field: 'value',
        flex: 1,
        sortable: false,
        headerName: 'Hit name',
      },
      {
        field: 'type',
        flex: 1,
        sortable: false,
        headerName: 'Type',
        renderCell: ({ row }) => formatTypeOfFailureString(row.type),
      },
      {
        field: 'reasons',
        flex: 1,
        sortable: false,
        headerName: 'Reason',
        renderCell: ({ row }) => {
          const { reasons } = row;

          return reasons
            .map((reason) => AmlMatchTypeLabels[reason] ?? reason)
            .map(furtherCased)
            .join(', ');
        },
      },
      {
        field: 'countryNames',
        flex: 1,
        sortable: false,
        headerName: 'Countries',
        renderCell: ({ row }) => formatCountryNamesString(row.countryNames),
      },
      {
        field: 'actions',
        flex: 1,
        sortable: false,
        headerName: 'Actions',
        renderCell: ({ row }) => (
          <TableActions
            actions={[
              {
                label: 'View',
                icon: Visibility,
                color: 'primary',
              },
              row.archived
                ? {
                    label: 'Unarchive',
                    tooltip: 'Unarchive',
                    visible: canEditAml,
                    icon: Unarchive,
                    onClick: (e) => {
                      e?.stopPropagation();
                      handleUnarchiveAmlHit(row.caDocId);
                    },
                  }
                : {
                    label: 'Archive',
                    tooltip: 'Archive',
                    visible: canEditAml,
                    color: 'error',
                    icon: Archive,
                    onClick: (e) => {
                      e?.stopPropagation();
                      handleArchiveAmlHit(row.caDocId);
                    },
                  },
            ]}
          />
        ),
      },
    ],
    [handleArchiveAmlHit, handleUnarchiveAmlHit, canEditAml],
  );

  const pagination = usePagination({
    id: 'aml-results',
    orderBy: 'createdAt',
    order: 'desc',
    pageSize: 10,
    page: 1,
  });

  // Memoize sorted results to prevent unnecessary recalculations
  const sortedResults = useMemo(() => {
    return sortBy(
      [...results],
      (result) => new Date(result.createdAt ?? ''),
    ).reverse();
  }, [results]);

  // Memoize paginated results
  const paginatedResults = useMemo(() => {
    return sortedResults.slice(
      (pagination.page - 1) * pagination.rowsPerPage,
      pagination.page * pagination.rowsPerPage,
    );
  }, [sortedResults, pagination.page, pagination.rowsPerPage]);

  // Memoize rows to prevent unnecessary recreations
  const rows = useMemo(() => {
    return paginatedResults.map((result) => ({
      ...result,
      isExpandable: true,
    }));
  }, [paginatedResults]);

  return (
    <TableContainer>
      <StyledTable
        id="aml-results"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.caDocId}
        fullHeight
        pagination={pagination}
        rowCount={sortedResults.length ?? 0}
        loading={isLoading}
        disableTableConfiguration
        disableSorting={true}
        getExpandedContent={(row) => {
          return (
            <AmlResultInformation
              caDocId={row.caDocId}
              investorId={investorId}
            />
          );
        }}
      />
    </TableContainer>
  );
};

export default AmlResultsTable;
